<template>
  <div class="format-settings">
    <div class="format-heading">
      <span class="etq-panel-kicker">Medidas físicas</span>
      <h5>Formato e página</h5>
      <p>Use as medidas indicadas pelo fabricante do rolo ou da folha.</p>
    </div>

    <section class="format-section">
      <div class="format-section-title">
        <span class="format-section-icon"><i class="fas fa-tag"></i></span>
        <div>
          <strong>Etiqueta</strong><small>Tamanho de cada unidade</small>
        </div>
      </div>

      <div class="measure-grid">
        <div class="form-group">
          <label>Largura</label>
          <div class="input-group input-group-sm">
            <input
              type="number"
              min="5"
              max="300"
              step="0.1"
              class="form-control"
              :value="label.largura_mm"
              @input="setNumber('etiqueta.largura_mm', $event.target.value)"
            />
            <div class="input-group-append">
              <span class="input-group-text">mm</span>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label>Altura</label>
          <div class="input-group input-group-sm">
            <input
              type="number"
              min="5"
              max="300"
              step="0.1"
              class="form-control"
              :value="label.altura_mm"
              @input="setNumber('etiqueta.altura_mm', $event.target.value)"
            />
            <div class="input-group-append">
              <span class="input-group-text">mm</span>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label>Área segura</label>
          <div class="input-group input-group-sm">
            <input
              type="number"
              min="0"
              max="20"
              step="0.1"
              class="form-control"
              :value="label.padding_mm"
              @input="setNumber('etiqueta.padding_mm', $event.target.value)"
            />
            <div class="input-group-append">
              <span class="input-group-text">mm</span>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label>Borda</label>
          <div class="input-group input-group-sm">
            <input
              type="number"
              min="0"
              max="2"
              step="0.1"
              class="form-control"
              :value="label.borda_mm"
              @input="setNumber('etiqueta.borda_mm', $event.target.value)"
            />
            <div class="input-group-append">
              <span class="input-group-text">mm</span>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-6 form-group mb-0">
          <label>Fundo</label>
          <div class="color-input">
            <input
              type="color"
              :value="label.fundo || '#FFFFFF'"
              @input="set('etiqueta.fundo', $event.target.value.toUpperCase())"
            />
            <span>{{ label.fundo || "#FFFFFF" }}</span>
          </div>
        </div>
        <div class="col-6 form-group mb-0">
          <label>Cantos arredondados</label>
          <div class="input-group input-group-sm">
            <input
              type="number"
              min="0"
              max="20"
              step="0.1"
              class="form-control"
              :value="label.cantos_arredondados_mm"
              @input="
                setNumber(
                  'etiqueta.cantos_arredondados_mm',
                  $event.target.value
                )
              "
            />
            <div class="input-group-append">
              <span class="input-group-text">mm</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="format-section">
      <div class="format-section-title">
        <span class="format-section-icon blue"
          ><i class="far fa-file-alt"></i
        ></span>
        <div>
          <strong>Página ou rolo</strong
          ><small>Distribuição para impressão</small>
        </div>
      </div>

      <div class="auto-roll-setting">
        <div>
          <strong>Ajustar rolo automaticamente</strong>
          <small
            >Mantém a página no tamanho exato da distribuição de
            etiquetas.</small
          >
        </div>
        <b-form-checkbox
          :checked="autoFitRoll"
          switch
          aria-label="Ajustar rolo automaticamente"
          @change="setAutoFitRoll"
        ></b-form-checkbox>
      </div>

      <div class="measure-grid">
        <div class="form-group">
          <label>Largura</label>
          <div class="input-group input-group-sm">
            <input
              type="number"
              min="10"
              max="500"
              step="0.1"
              class="form-control"
              :value="page.largura_mm"
              @input="setNumber('pagina.largura_mm', $event.target.value)"
            />
            <div class="input-group-append">
              <span class="input-group-text">mm</span>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label>Altura</label>
          <div class="input-group input-group-sm">
            <input
              type="number"
              min="10"
              max="500"
              step="0.1"
              class="form-control"
              :value="page.altura_mm"
              @input="setNumber('pagina.altura_mm', $event.target.value)"
            />
            <div class="input-group-append">
              <span class="input-group-text">mm</span>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label>Colunas</label>
          <input
            type="number"
            min="1"
            max="10"
            step="1"
            class="form-control form-control-sm"
            :value="page.colunas"
            @input="setInteger('pagina.colunas', $event.target.value)"
          />
        </div>
        <div class="form-group">
          <label>Linhas</label>
          <input
            type="number"
            min="1"
            max="100"
            step="1"
            class="form-control form-control-sm"
            :value="page.linhas"
            @input="setInteger('pagina.linhas', $event.target.value)"
          />
        </div>
      </div>

      <button
        type="button"
        class="advanced-toggle"
        @click="advanced = !advanced"
      >
        <span>Margens e espaçamentos</span>
        <i :class="advanced ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
      </button>

      <div v-if="advanced" class="advanced-box">
        <span class="advanced-label">Margens</span>
        <div class="measure-grid">
          <div v-for="margin in margins" :key="margin.key" class="form-group">
            <label>{{ margin.label }}</label>
            <div class="input-group input-group-sm">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                class="form-control"
                :value="page[margin.key]"
                @input="setNumber(`pagina.${margin.key}`, $event.target.value)"
              />
              <div class="input-group-append">
                <span class="input-group-text">mm</span>
              </div>
            </div>
          </div>
        </div>
        <span class="advanced-label">Entre etiquetas</span>
        <div class="measure-grid mb-0">
          <div class="form-group mb-0">
            <label>Horizontal</label>
            <div class="input-group input-group-sm">
              <input
                type="number"
                min="0"
                max="50"
                step="0.1"
                class="form-control"
                :value="page.espacamento_horizontal_mm"
                @input="
                  setNumber(
                    'pagina.espacamento_horizontal_mm',
                    $event.target.value
                  )
                "
              />
              <div class="input-group-append">
                <span class="input-group-text">mm</span>
              </div>
            </div>
          </div>
          <div class="form-group mb-0">
            <label>Vertical</label>
            <div class="input-group input-group-sm">
              <input
                type="number"
                min="0"
                max="50"
                step="0.1"
                class="form-control"
                :value="page.espacamento_vertical_mm"
                @input="
                  setNumber(
                    'pagina.espacamento_vertical_mm',
                    $event.target.value
                  )
                "
              />
              <div class="input-group-append">
                <span class="input-group-text">mm</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="fit-summary" :class="fitsPage ? 'fits' : 'overflow'">
      <i
        :class="
          fitsPage ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'
        "
      ></i>
      <div>
        <strong>{{
          fitsPage
            ? "Distribuição cabe na página"
            : "Distribuição ultrapassa a página"
        }}</strong>
        <small
          >{{ totalLabels }} etiqueta{{ totalLabels === 1 ? "" : "s" }} por
          página · uso de {{ usedWidth }} × {{ usedHeight }} mm</small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { clone } from "@/modules/Etiquetas/services/etiquetaDefinition";

export default {
  name: "EtiquetaFormatSettings",
  props: {
    definition: { type: Object, required: true },
  },
  data() {
    return {
      advanced: false,
      autoFitRoll: false,
      margins: [
        { key: "margem_superior_mm", label: "Superior" },
        { key: "margem_direita_mm", label: "Direita" },
        { key: "margem_inferior_mm", label: "Inferior" },
        { key: "margem_esquerda_mm", label: "Esquerda" },
      ],
    };
  },
  created() {
    this.autoFitRoll = this.shouldStartWithAutoFit();
  },
  computed: {
    page() {
      return this.definition.pagina || {};
    },
    label() {
      return this.definition.etiqueta || {};
    },
    totalLabels() {
      return Number(this.page.colunas || 1) * Number(this.page.linhas || 1);
    },
    usedWidthNumber() {
      return (
        Number(this.page.margem_esquerda_mm || 0) +
        Number(this.page.margem_direita_mm || 0) +
        Number(this.page.colunas || 1) * Number(this.label.largura_mm || 0) +
        Math.max(0, Number(this.page.colunas || 1) - 1) *
          Number(this.page.espacamento_horizontal_mm || 0)
      );
    },
    usedHeightNumber() {
      return (
        Number(this.page.margem_superior_mm || 0) +
        Number(this.page.margem_inferior_mm || 0) +
        Number(this.page.linhas || 1) * Number(this.label.altura_mm || 0) +
        Math.max(0, Number(this.page.linhas || 1) - 1) *
          Number(this.page.espacamento_vertical_mm || 0)
      );
    },
    usedWidth() {
      return this.usedWidthNumber.toFixed(1).replace(".0", "");
    },
    usedHeight() {
      return this.usedHeightNumber.toFixed(1).replace(".0", "");
    },
    fitsPage() {
      return (
        this.usedWidthNumber <= Number(this.page.largura_mm || 0) + 0.5 &&
        this.usedHeightNumber <= Number(this.page.altura_mm || 0) + 0.5
      );
    },
  },
  methods: {
    set(path, value, { manualPageSize = false } = {}) {
      const next = clone(this.definition);
      this.setPath(next, path, value);

      if (manualPageSize) this.autoFitRoll = false;
      else if (this.autoFitRoll && this.affectsDistribution(path)) {
        this.fitPageToDistribution(next);
      }

      this.emitUpdate(next);
    },
    setNumber(path, value) {
      const number = Number(value);
      if (!Number.isNaN(number)) {
        this.set(path, number, {
          manualPageSize: ["pagina.largura_mm", "pagina.altura_mm"].includes(
            path
          ),
        });
      }
    },
    setInteger(path, value) {
      const number = Number.parseInt(value, 10);
      if (!Number.isNaN(number)) this.set(path, number);
    },
    setAutoFitRoll(enabled) {
      this.autoFitRoll = Boolean(enabled);
      if (!this.autoFitRoll) return;

      const next = clone(this.definition);
      this.fitPageToDistribution(next);
      this.emitUpdate(next);
    },
    shouldStartWithAutoFit() {
      const usage = this.distributionSize(this.definition);
      const page = this.definition.pagina || {};
      const rows = Number(page.linhas || 1);

      return (
        rows === 1 &&
        Math.abs(Number(page.largura_mm || 0) - usage.width) <= 0.5 &&
        Math.abs(Number(page.altura_mm || 0) - usage.height) <= 0.5
      );
    },
    affectsDistribution(path) {
      return [
        "etiqueta.largura_mm",
        "etiqueta.altura_mm",
        "pagina.colunas",
        "pagina.linhas",
        "pagina.margem_superior_mm",
        "pagina.margem_direita_mm",
        "pagina.margem_inferior_mm",
        "pagina.margem_esquerda_mm",
        "pagina.espacamento_horizontal_mm",
        "pagina.espacamento_vertical_mm",
      ].includes(path);
    },
    distributionSize(definition) {
      const page = definition.pagina || {};
      const label = definition.etiqueta || {};
      const columns = Math.max(1, Number(page.colunas || 1));
      const rows = Math.max(1, Number(page.linhas || 1));
      const width =
        Number(page.margem_esquerda_mm || 0) +
        Number(page.margem_direita_mm || 0) +
        columns * Number(label.largura_mm || 0) +
        Math.max(0, columns - 1) * Number(page.espacamento_horizontal_mm || 0);
      const height =
        Number(page.margem_superior_mm || 0) +
        Number(page.margem_inferior_mm || 0) +
        rows * Number(label.altura_mm || 0) +
        Math.max(0, rows - 1) * Number(page.espacamento_vertical_mm || 0);

      return {
        width: Number(width.toFixed(2)),
        height: Number(height.toFixed(2)),
      };
    },
    fitPageToDistribution(definition) {
      if (!definition.pagina) this.$set(definition, "pagina", {});
      const usage = this.distributionSize(definition);
      definition.pagina.largura_mm = usage.width;
      definition.pagina.altura_mm = usage.height;
    },
    setPath(target, path, value) {
      const parts = path.split(".");
      const last = parts.pop();
      let current = target;
      parts.forEach((part) => {
        if (!current[part]) this.$set(current, part, {});
        current = current[part];
      });
      current[last] = value;
    },
    emitUpdate(next) {
      this.$emit("change-start");
      this.$emit("update", next);
    },
  },
};
</script>

<style lang="scss" scoped>
.format-settings {
  padding: 20px;
}

.etq-panel-kicker {
  color: #7e8299;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.format-heading {
  h5 {
    margin: 2px 0 5px;
    color: #263238;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: #7e8299;
    font-size: 11px;
    line-height: 1.45;
  }
}

.format-section {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #eef0f5;
}

.format-section-title {
  display: flex;
  align-items: center;
  margin-bottom: 14px;

  strong,
  small {
    display: block;
  }

  strong {
    color: #3f4254;
    font-size: 12px;
  }

  small {
    color: #a1a5b7;
    font-size: 10px;
  }
}

.format-section-icon {
  width: 34px;
  height: 34px;
  margin-right: 10px;
  border-radius: 8px;
  background: #f0ebff;
  color: #8950fc;
  display: grid;
  place-items: center;

  &.blue {
    background: #eaf5ff;
    color: #3699ff;
  }
}

.measure-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 10px;
}

.auto-roll-setting {
  margin: -2px 0 15px;
  padding: 11px 12px;
  border: 1px solid #e1d8fb;
  border-radius: 8px;
  background: #f8f5ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  strong,
  small {
    display: block;
  }

  strong {
    color: #4f3b78;
    font-size: 11px;
  }

  small {
    max-width: 190px;
    margin-top: 2px;
    color: #7e8299;
    font-size: 9px;
    line-height: 1.4;
  }

  ::v-deep .custom-control {
    flex: 0 0 auto;
    min-height: 20px;
  }
}

label {
  color: #5e6278;
  font-size: 10px;
  font-weight: 600;
  margin-bottom: 4px;
}

.color-input {
  height: 32px;
  padding: 4px;
  border: 1px solid #e4e6ef;
  border-radius: 5px;
  display: flex;
  align-items: center;
  gap: 7px;

  input {
    width: 24px;
    height: 22px;
    border: 0;
    padding: 0;
    background: transparent;
  }

  span {
    color: #7e8299;
    font-size: 9px;
  }
}

.advanced-toggle {
  width: 100%;
  border: 0;
  background: transparent;
  color: #5e6278;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 11px;
  font-weight: 600;
}

.advanced-box {
  padding: 13px;
  border-radius: 8px;
  background: #f7f8fb;
}

.advanced-label {
  display: block;
  margin-bottom: 8px;
  color: #a1a5b7;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.fit-summary {
  margin-top: 18px;
  padding: 12px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  gap: 10px;

  i {
    font-size: 17px;
  }

  strong,
  small {
    display: block;
  }

  strong {
    font-size: 11px;
  }

  small {
    margin-top: 2px;
    font-size: 9px;
  }

  &.fits {
    background: #e8fff3;
    color: #169f6c;
  }

  &.overflow {
    background: #fff4de;
    color: #b76e00;
  }
}
</style>
